import React, { useState, useEffect } from "react";
import { FileText, Loader2, CheckCircle2, RefreshCw, Pencil, Mic, AlertTriangle, Play, Square } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { unlock, playOn } from "@/components/voiceKit";

let previewAudio = null;

const VOICE_RE = /^((?:التعليق الصوتي|التعليق)\s*(?:\([^)]*\))?)\s*[:：]\s*(.*)$/;
/** يستبدل سطر التعليق الصوتي رقم idx في النص الكامل (يحافظ على التسمية) */
const replaceVoiceLine = (text, idx, value) => {
  let k = -1;
  return (text || "").split("\n").map((l) => {
    const m = l.replace(/^\s+/, "").match(VOICE_RE);
    if (!m) return l;
    k += 1;
    return k === idx ? `${m[1]}: ${value}` : l;
  }).join("\n");
};
export const voiceLines = (text) => (text || "").split("\n").map((l) => l.replace(/^\s+/, "")).filter((l) => /^(التعليق الصوتي|التعليق)\s*(\([^)]*\))?\s*[:：]/.test(l)).map((l) => l.replace(/^(التعليق الصوتي|التعليق)\s*(\([^)]*\))?\s*[:：]\s*/, ""));
/** نص حر بلا سطور تعليق: جمله هي ما يُنطق حرفيًا (نفس قاعدة المخرج في الخادم). */
export const freeLines = (text) => (text || "").split(/(?<=[.!؟?])\s+|\n+/).map((s) => s.trim()).filter((s) => s.split(/\s+/).length >= 2);
/** يستبدل الجملة رقم idx في النص الحر (بنفس تقسيم freeLines) مع الحفاظ على بقية النص والفواصل */
export const replaceFreeLine = (text, idx, value) => {
  let k = -1;
  return (text || "").split(/((?<=[.!؟?])\s+|\n+)/).map((p, i) => {
    if (i % 2 === 1) return p;
    const s = p.trim();
    if (s.split(/\s+/).length < 2) return p;
    k += 1;
    return k === idx ? p.replace(s, value) : p;
  }).join("");
};

/** تجربة الأداء: نفس النص والصوت والتشكيل التي سيستخدمها الفيديو — يسمعها العميل ويصحّح قبل صرف رصيد الفيديو. */
const VoicePreviewBtn = ({ campaignId, postId, text, voice, style = "", onPreviewed, request }) => {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const stop = () => { if (previewAudio) { previewAudio.pause(); previewAudio.currentTime = 0; } setPlaying(false); };
  const play = async () => {
    if (playing) return stop();
    if (!voice) return toast.error("اختر الصوت أولاً من لوحة الشخصية والصوت");
    if (!previewAudio) previewAudio = new Audio();
    unlock(previewAudio);
    setBusy(true);
    try {
      const r = request
        ? await request({ voice, style, script: text })
        : await api.post(`/campaigns/${campaignId}/posts/${postId}/voice-preview`, { voice, style, script: text }, { responseType: "blob", timeout: 180000 });
      previewAudio.onended = () => setPlaying(false);
      previewAudio.onpause = () => setPlaying(false);
      playOn(previewAudio, URL.createObjectURL(r.data));
      setPlaying(true);
      onPreviewed && onPreviewed();
    } catch (e) {
      let d = e.response?.data;
      if (d instanceof Blob) { try { d = JSON.parse(await d.text()); } catch { d = null; } }
      toast.error(formatError(d?.detail) || "تعذّر توليد الصوت");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" onClick={play} disabled={busy} className="h-7 text-xs gap-1 bg-amber-700 hover:bg-amber-800 text-white" data-testid={`voice-preview-${postId}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : playing ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />}
      {busy ? "يتجهّز الصوت…" : playing ? "إيقاف" : "١· جرّب الصوت المختار"}
    </Button>
  );
};

/** سطور التعليق الصوتي مميّزة: هي ما يُنطق حرفيًا في الفيديو — تنبيه للعميل بمراجعتها. */
export const VoiceoverBox = ({ text, postId, campaignId, voice, style = "", onChangeLine, onSave, dirty, previewed, onPreviewed, voiceApproved, onApproveVoice, approvingVoice, request, free = false }) => {
  const lines = voiceLines(text).length ? voiceLines(text) : (free ? freeLines(text) : []);
  if (!lines.length) return null;
  const editable = !!onChangeLine && (voiceLines(text).length > 0 || free);
  const step = voiceApproved && !dirty ? 3 : previewed && !dirty ? 2 : 1;
  return (
    <div className="rounded-lg border border-amber-600/60 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-500/10 p-2.5 space-y-1.5" data-testid={`voiceover-box-${postId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800 dark:text-amber-300"><Mic className="w-3.5 h-3.5" /> التعليق الصوتي — هذا ما سيُنطق حرفيًا في الفيديو</div>
      </div>
      <ol className="space-y-1.5 ps-4 list-decimal marker:text-amber-700 dark:marker:text-amber-400">
        {lines.map((l, i) => (
          <li key={i} className="text-sm font-semibold leading-relaxed text-foreground">
            {editable
              ? <input value={l} onChange={(e) => onChangeLine(i, e.target.value)} dir="rtl"
                  className="w-full bg-white/70 dark:bg-black/20 border border-amber-500/40 focus:border-amber-600 rounded-md px-2 py-1 text-sm font-semibold outline-none"
                  data-testid={`voiceover-line-${postId}-${i}`} />
              : <span data-testid={`voiceover-line-${postId}-${i}`}>{l}</span>}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        {(campaignId || request) && <VoicePreviewBtn campaignId={campaignId} postId={postId} text={text} voice={voice} style={style} onPreviewed={onPreviewed} request={request} />}
        {onSave && dirty && (
          <Button size="sm" onClick={onSave} className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`voiceover-save-${postId}`}>
            <CheckCircle2 className="w-3 h-3" /> احفظ التصحيح واعتمد
          </Button>
        )}
        {onApproveVoice && !dirty && (step === 3
          ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 dark:text-emerald-400" data-testid={`voice-approved-${postId}`}><CheckCircle2 className="w-3.5 h-3.5" /> ٢· صوت التوليد محفوظ — ٣· اضغط «ولّد الفيديو» بالأسفل</span>
          : <Button size="sm" onClick={onApproveVoice} disabled={!previewed || approvingVoice} title={previewed ? "" : "اسمع التعليق أولاً"}
              className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50" data-testid={`voice-approve-${postId}`}>
              {approvingVoice ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} ٢· احفظ صوت التوليد
            </Button>)}
      </div>
      <p className="text-[11px] text-amber-900/80 dark:text-amber-200/90 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {step === 1 ? "الخطوة ١: اختر الشخصية والصوت والأسلوب من اللوحة أعلاه، عدّل أي كلمة هنا، ثم اضغط «جرّب الصوت» — زر التجربة الوحيد." : step === 2 ? "الخطوة ٢: أعجبك الأداء؟ احفظ صوت التوليد — هذا التسجيل نفسه يُستخدم في الفيديو حرفيًا. وإن عدّلت كلمة أعد الاستماع." : "الخطوة ٣: كل شيء جاهز — ولّد الفيديو من قسم الإنشاء بالأسفل."}</p>
    </div>
  );
};

/** سكربت فيديو المنشور (3 مشاهد ≈ 15 ث): ولّد → راجع/عدّل → اعتمد. المخرج يلتزم به حرفياً. */
export const PostScript = ({ campaignId, post, onPatch, disabled, voice = "", style = "" }) => {
  const [text, setText] = useState(post.script || "");
  const [shown, setShown] = useState(post.script || "");  // صندوق التعليق يتحدّث بعد توقف الكتابة — لا قفزات للشاشة أثناء الكتابة على الجوال
  useEffect(() => { const t = setTimeout(() => setShown(text), 700); return () => clearTimeout(t); }, [text]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const approved = !!post.script_approved && !!(post.script || "").trim();
  const dirty = text !== (post.script || "");
  const vp = post.voice_preview || {};
  // تغيير الصوت أو الأسلوب بعد الاستماع يُبطل المعاينة: ما يسمعه العميل هو ما يُنتج حرفيًا
  const sameVoice = !vp.voice || (vp.voice === voice && (vp.style || "") === (style || ""));
  const [previewed, setPreviewed] = useState(!!vp.url);
  const [approvingVoice, setApprovingVoice] = useState(false);
  const voiceApproved = !!vp.approved && sameVoice;
  const approveVoice = async () => {
    setApprovingVoice(true);
    try {
      await api.put(`/campaigns/${campaignId}/posts/${post.id}/voice-approve`);
      onPatch(campaignId, post.id, { voice_preview: { ...(post.voice_preview || {}), approved: true } });
      toast.success("حُفظ صوت التوليد — الخطوة التالية: ولّد الفيديو");
      setTimeout(() => document.querySelector(`[data-testid="gen-video-${post.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setApprovingVoice(false); }
  };
  const changeLine = (i, v) => { const t = replaceVoiceLine(text, i, v); setText(t); setShown(t); };
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/posts/${post.id}/script`);
      setText(data.script); setEditing(true);
      onPatch(campaignId, post.id, { script: data.script, script_approved: false });
      toast.success("تم توليد السكربت — راجعه وعدّله ثم اعتمده");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try {
      await api.put(`/campaigns/${campaignId}/posts/${post.id}/script`, { script: text });
      onPatch(campaignId, post.id, { script: text, script_approved: true });
      setEditing(false);
      toast.success("اعتُمد السكربت — الفيديو سيُبنى عليه حرفياً");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className={`mt-3 rounded-xl border p-3 space-y-2 ${approved && !editing ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`} data-testid={`script-box-${post.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-bold flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> سكربت الفيديو (٣ مشاهد ≈ ١٥ ثانية)
          {approved && !editing && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> معتمد</span>}
        </span>
        <div className="flex gap-1.5 flex-wrap">
          {!text && <Button size="sm" onClick={gen} disabled={busy || disabled} title={disabled ? "ولّد النص والهاشتاقات أولاً" : ""} className="h-7 text-xs gap-1 bg-rose-600 hover:bg-rose-700 text-white" data-testid={`script-gen-${post.id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} ولّد السكربت</Button>}
          {text && !editing && <Button size="sm" className="h-7 text-xs gap-1 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => setEditing(true)} data-testid={`script-edit-${post.id}`}><Pencil className="w-3 h-3" /> صحّح السكربت</Button>}
          {text && <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-border" onClick={gen} disabled={busy} data-testid={`script-regen-${post.id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} أعد التوليد</Button>}
          {text && (editing || !approved) && <Button size="sm" onClick={approve} disabled={busy || !text.trim()} className="h-7 text-xs gap-1 bg-rose-600 hover:bg-rose-700 text-white" data-testid={`script-approve-${post.id}`}><CheckCircle2 className="w-3 h-3" /> اعتمد السكربت</Button>}
        </div>
      </div>
      {shown && <VoiceoverBox text={shown} postId={post.id} campaignId={campaignId} voice={voice} style={style} onChangeLine={changeLine} onSave={approve} dirty={dirty || !approved} previewed={previewed && sameVoice} onPreviewed={() => { setPreviewed(true); onPatch(campaignId, post.id, { voice_preview: { ...(post.voice_preview || {}), voice, style, url: "pending", approved: false } }); }} voiceApproved={voiceApproved} onApproveVoice={approveVoice} approvingVoice={approvingVoice} />}
      {text && (editing || !approved) && (
        <>
          <p className="text-[11px] text-muted-foreground">صحّح المشاهد والعنوان هنا؛ سطور «التعليق الصوتي:» تُعدَّل من المربع الأصفر أعلاه أو هنا — ثم اضغط «اعتمد السكربت».</p>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} className="bg-secondary border-border text-sm leading-relaxed whitespace-pre-wrap min-h-[260px] resize-y" data-testid={`script-text-${post.id}`} />
        </>
      )}
      {text && approved && !editing && <pre className="text-[11px] text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed max-h-28 overflow-y-auto" data-testid={`script-preview-${post.id}`}>{text}</pre>}
      {!text && <p className="text-[11px] text-muted-foreground">يُكتب السكربت من نص المنشور ونوع الحملة ومنتجك، وتُنطق جمله حرفياً في الفيديو ويظهر المنتج في مشاهده.</p>}
    </div>
  );
};
